/**
 * ContratoFrontApiSection — Onda 3 do plano de qualidade.
 *
 * Mostra, na Conferência de Entrega, o contrato front ↔ API por superfície: cada
 * funcionalidade (tela × ação × endpoint) com status determinístico, vindo do grafo
 * estático das chamadas do front cruzado com os endpoints do backend. Nada aqui vem
 * de LLM. Com `modo === 'e2e'` (Onda 4), o "ok" passa a vir do teste no navegador.
 *
 * `matriz` null/undefined → não renderiza nada.
 *
 * Standards: Clean, TDD.
 */
import { useMemo, useState } from 'react';
import { C } from '@/theme/palette';
import { useExpandirTudo } from '@/components/ExportacaoContext';
import {
  GruposConferencia, NOTA_COMPLEMENTARES, COR_GRUPO, GRUPO_COMPLEMENTAR, grupoDoItem, contarGrupos,
} from '@/components/GruposConferencia';

export const ORDEM_STATUS_CONTRATO = ['ok', 'quebrada', 'faltante', 'nao_verificado'];

const COR_STATUS = {
  ok: C.cyber,
  quebrada: C.hot,
  faltante: C.warn,
  nao_verificado: C.neutralDim,
};

const ROTULO_STATUS = {
  quebrada: 'quebrada',
  faltante: 'faltante',
  nao_verificado: 'não verificada',
};

const ROTULO_SUPERFICIE = { web: 'Web', mobile: 'Mobile' };
const ORDEM_SUPERFICIE = ['web', 'mobile'];

export const NOTA_ESTATICO =
  'Estático: o endpoint existe e a chamada casa método e caminho. A prova no navegador chega com o E2E.';

/** Rótulo do status; "ok" vira "ok estático" enquanto a matriz for estática. */
export function rotuloStatus(status, modo) {
  if (status === 'ok') return modo === 'estatico' ? 'ok estático' : 'ok';
  return ROTULO_STATUS[status] ?? (status || 'desconhecido');
}

function corStatus(status) {
  return COR_STATUS[status] ?? C.neutralDim;
}

function rotuloSuperficie(s) {
  return ROTULO_SUPERFICIE[s] ?? s;
}

/** Agrupa por tela (`screen`), em ordem alfabética pt-BR. */
export function agruparPorTela(itens) {
  const grupos = new Map();
  for (const it of itens) {
    const chave = it.screen || 'sem tela';
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave).push(it);
  }
  return [...grupos.entries()].sort(([a], [b]) => a.localeCompare(b, 'pt-BR'));
}

function fmt(n) {
  return Number(n ?? 0).toLocaleString('pt-BR');
}

function CartaoSuperficie({ superficie, contagem, grupos, modo }) {
  const partes = ORDEM_STATUS_CONTRATO.map((s) => ({ s, n: Number(contagem?.[s] ?? 0) }));
  return (
    <div data-testid={`cartao-${superficie}`} style={{ flex: '1 1 240px', minWidth: 0,
      border: `1px solid ${C.blackBorder}`, borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
        {rotuloSuperficie(superficie)}
      </div>
      <div data-testid={`resumo-${superficie}`} style={{ fontSize: 12.5, color: C.neutralDim }}>
        {partes.map(({ s, n }, i) => (
          <span key={s}>
            {i > 0 && <span style={{ color: C.neutralMuted }}> · </span>}
            <span style={{ color: n > 0 ? corStatus(s) : C.neutralMuted }}>
              {fmt(n)} {rotuloStatus(s, modo)}{s !== 'ok' && n !== 1 ? 's' : ''}
            </span>
          </span>
        ))}
      </div>
      {grupos && (
        <div data-testid={`grupos-${superficie}`} style={{ marginTop: 8 }}>
          <GruposConferencia contagem={grupos} rotuloOk={rotuloStatus('ok', modo)} />
        </div>
      )}
    </div>
  );
}

function LinhaFuncionalidade({ item, modo, mostrarSuperficie }) {
  // O que falta do lado do backend é atividade complementar (a tela está certa): cor informativa.
  const complementar = grupoDoItem(item) === GRUPO_COMPLEMENTAR;
  const color = complementar ? COR_GRUPO.complementar : corStatus(item.status);
  return (
    <div data-testid="funcionalidade" style={{ padding: '8px 16px 8px 34px', borderTop: `1px solid ${C.blackBorder}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: C.text }}>{item.featureRef}</span>
        <span style={{ fontSize: 12.5, color: C.text }}>{item.action}</span>
        <span style={{ fontFamily: 'monospace', fontSize: 11.5, color: C.neutralDim, wordBreak: 'break-all' }}>
          {item.method} {item.path}
        </span>
        {complementar && (
          <span data-testid="tag-complementar" style={{ fontSize: 10.5, color: COR_GRUPO.complementar }}>
            atividade complementar do backend
          </span>
        )}
        {mostrarSuperficie && (
          <span style={{ fontSize: 11, color: C.neutralMuted }}>{rotuloSuperficie(item.surface)}</span>
        )}
        <span data-testid="status-funcionalidade" style={{ fontFamily: 'monospace', fontSize: 10.5,
          padding: '2px 8px', borderRadius: 5, marginLeft: 'auto',
          color, background: `${color}1F`, border: `1px solid ${color}55` }}>
          {rotuloStatus(item.status, modo)}
        </span>
      </div>
      {item.status !== 'ok' && item.evidence && (
        <div data-testid="evidencia-contrato" style={{ fontSize: 11.5, color: C.neutralDim, marginTop: 4,
          fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {item.evidence}
        </div>
      )}
    </div>
  );
}

function GrupoTela({ nome, itens, modo, mostrarSuperficie }) {
  const ok = itens.filter((i) => i.status === 'ok').length;
  const correcoes = itens.filter((i) => grupoDoItem(i) === 'correcao').length;
  const corResumo = correcoes ? C.warn : (ok < itens.length ? COR_GRUPO.complementar : C.cyber);
  return (
    <div data-testid="grupo-tela" style={{ borderTop: `1px solid ${C.blackBorder}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px' }}>
        <span data-testid="nome-tela" style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{nome}</span>
        <span style={{ fontSize: 11.5, color: corResumo, marginLeft: 'auto' }}>
          {fmt(ok)}/{fmt(itens.length)} ok
        </span>
      </div>
      {itens.map((it, i) => (
        <LinhaFuncionalidade key={`${it.featureRef}-${it.surface}-${it.method}-${it.path}-${i}`}
          item={it} modo={modo} mostrarSuperficie={mostrarSuperficie} />
      ))}
    </div>
  );
}

function BotaoFiltro({ ativo, onClick, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={ativo} data-export="estatico"
      style={{ fontSize: 11.5, padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
        color: ativo ? C.cyber : C.neutralDim, background: ativo ? `${C.cyber}1F` : 'transparent',
        border: `1px solid ${ativo ? `${C.cyber}55` : C.blackBorder}` }}>
      {children}
    </button>
  );
}

export function ContratoFrontApiSection({ matriz }) {
  const itens = useMemo(() => matriz?.itens ?? [], [matriz]);
  const modo = matriz?.modo ?? 'estatico';
  const temProblema = itens.some((i) => i.status !== 'ok');
  // Padrão segue os dados (ligado se houver algo não ok) até o usuário mexer.
  const [escolha, setSoProblemas] = useState(null);
  const [escolhaSuperficie, setSuperficie] = useState('todas');
  // Exportação: todas as superfícies, sem o filtro (as escolhas do usuário ficam guardadas).
  const expandirTudo = useExpandirTudo();
  const soProblemas = expandirTudo ? false : (escolha ?? temProblema);
  const superficie = expandirTudo ? 'todas' : escolhaSuperficie;

  const porSuperficie = matriz?.porSuperficie ?? {};
  const porGrupoSuperficie = matriz?.porGrupoSuperficie ?? null;
  const porGrupo = matriz?.porGrupo ?? contarGrupos(itens);
  const superficies = useMemo(() => {
    const chaves = new Set([...Object.keys(porSuperficie), ...itens.map((i) => i.surface).filter(Boolean)]);
    return [...chaves].sort((a, b) => {
      const ia = ORDEM_SUPERFICIE.indexOf(a); const ib = ORDEM_SUPERFICIE.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b, 'pt-BR');
    });
  }, [porSuperficie, itens]);
  const variasSuperficies = superficies.length > 1;

  const grupos = useMemo(() => {
    let lista = itens;
    if (superficie !== 'todas') lista = lista.filter((i) => i.surface === superficie);
    if (soProblemas) lista = lista.filter((i) => i.status !== 'ok');
    return agruparPorTela(lista);
  }, [itens, superficie, soProblemas]);

  if (!matriz) return null;

  return (
    <div data-testid="contrato-front-api" style={{ marginTop: 16, border: `1px solid ${C.blackBorder}`,
      background: C.blackElev, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: C.cyber }}>⇄</span>
          <h3 style={{ margin: 0, fontSize: 14, color: C.text }}>Contrato front ↔ API por superfície</h3>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {Object.keys(porSuperficie).length > 0
            ? superficies.filter((s) => porSuperficie[s])
              .map((s) => <CartaoSuperficie key={s} superficie={s} contagem={porSuperficie[s]}
                grupos={porGrupoSuperficie?.[s]} modo={modo} />)
            : <span style={{ fontSize: 12, color: C.neutralMuted }}>Nenhuma superfície registrada.</span>}
        </div>
        <div data-testid="contrato-grupos" style={{ marginTop: 10 }}>
          <GruposConferencia contagem={porGrupo} rotuloOk={rotuloStatus('ok', modo)} />
        </div>
        {Number(porGrupo.complementares ?? 0) > 0 && (
          <p style={{ fontSize: 12, color: C.neutralDim, margin: '8px 0 0' }}>{NOTA_COMPLEMENTARES}</p>
        )}
        {modo === 'estatico' && (
          <p data-testid="nota-estatico" style={{ fontSize: 12, color: C.neutralMuted, margin: '8px 0 0' }}>
            {NOTA_ESTATICO}
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
          <label data-export="omitir" style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: C.neutralDim, cursor: 'pointer' }}>
            <input type="checkbox" checked={soProblemas} onChange={(e) => setSoProblemas(e.target.checked)} />
            só problemas
          </label>
          {variasSuperficies && (
            <div role="group" aria-label="Filtrar por superfície" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <BotaoFiltro ativo={superficie === 'todas'} onClick={() => setSuperficie('todas')}>Todas</BotaoFiltro>
              {superficies.map((s) => (
                <BotaoFiltro key={s} ativo={superficie === s} onClick={() => setSuperficie(s)}>
                  {rotuloSuperficie(s)}
                </BotaoFiltro>
              ))}
            </div>
          )}
        </div>
      </div>
      {grupos.length === 0 ? (
        <div style={{ padding: '10px 16px 14px', fontSize: 12, color: C.neutralMuted,
          borderTop: `1px solid ${C.blackBorder}` }}>
          {soProblemas ? 'Nenhum problema no contrato front ↔ API.' : 'Nenhuma funcionalidade registrada.'}
        </div>
      ) : (
        grupos.map(([nome, lista]) => (
          <GrupoTela key={nome} nome={nome} itens={lista} modo={modo}
            mostrarSuperficie={variasSuperficies && superficie === 'todas'} />
        ))
      )}
    </div>
  );
}

export default ContratoFrontApiSection;
